import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import SpiningLoader from '@/components/common/SpinningLoader';
import type {BusinessLimit, BusinessLimitType} from '@/types';
import {BusinessLimitFrequency} from '@/types';
import * as formatDate from '@/utils/date';
import {formatSelectOptionItem} from '@/utils/merchant';

import LimitUpdateButton from './LimitUpdateButton';

type LimitSummaryProp = {
  item: BusinessLimit;
  isLoading?: boolean;
  isDetail?: boolean;
  businessId?: string;
  dataCy?: string;
};

// When each frequency's total starts again; ONCE checks one transaction.
const frequencyLabels: Record<BusinessLimitFrequency, string> = {
  [BusinessLimitFrequency.Once]: 'Once (each transaction)',
  [BusinessLimitFrequency.Daily]: 'Daily (resets at 00:00 UTC)',
  [BusinessLimitFrequency.Weekly]: 'Weekly (resets Monday 00:00 UTC)',
  [BusinessLimitFrequency.Monthly]: 'Monthly (resets on the 1st, 00:00 UTC)',
};

/**
 * `Max 2,500 USD / Min 10 USD` for an amount limit, `Max 20 transactions` for
 * a count limit: the summary line for one frequency entry. Amounts are always
 * in USD, whatever currency the limit's tags name.
 */
const formatLimitEntry = (entry?: BusinessLimitType): string => {
  const isAmount = entry?.type === 'AMOUNT';
  const bounds: string[] = [];

  if (typeof entry?.value?.max === 'number') {
    const max = entry.value.max.toLocaleString('en-US');
    bounds.push(isAmount ? `Max ${max} USD` : `Max ${max} transactions`);
  }
  if (typeof entry?.value?.min === 'number') {
    const min = entry.value.min.toLocaleString('en-US');
    bounds.push(isAmount ? `Min ${min} USD` : `Min ${min} (not checked)`);
  }

  return bounds.join(' / ');
};

/** `From Currency equals NGN`, one tag of the rule, in words. */
const formatTag = (field: string, expression: string): string => {
  const [operator, value] = expression.split('::');
  return `${formatSelectOptionItem(field)} ${operator.replace(/_/g, ' ').toLowerCase()} ${value}`;
};

const LimitSummary = ({
  item,
  isLoading,
  businessId,
  dataCy,
}: LimitSummaryProp) => {
  const limitEntries = Object.entries(item?.limit || {});
  const tags = Object.entries(item?.meta?.tags ?? {});
  return isLoading ? (
    <SpiningLoader />
  ) : (
    <div
      data-cy={dataCy}
      className="bg-white dark:bg-transparent rounded-lg w-full px-5 sm:px-5 py-5 sm:py-5"
    >
      <div className="flex items-center gap-4 mb-3">
        <h2
          data-cy={dataCy ? `${dataCy}-name` : undefined}
          className="font-semibold text-xl mr-3"
        >
          {item.name}
        </h2>
      </div>

      <div>
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">Limit ID</p>
          <p
            data-cy={dataCy ? `${dataCy}-limit-id` : undefined}
            className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
          >
            {item?.id}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">Name</p>
          <p
            data-cy={dataCy ? `${dataCy}-display-name` : undefined}
            className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
          >
            {item?.name}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">Status</p>
          <p
            data-cy={dataCy ? `${dataCy}-status` : undefined}
            className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
          >
            {!item?.meta?.flags?.isDeactivated ? (
              <span className="text-green-500">Active</span>
            ) : (
              <span className="text-red-500">Inactive</span>
            )}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Applies to
          </p>
          <p
            data-cy={dataCy ? `${dataCy}-applies-to` : undefined}
            className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
          >
            {tags.length
              ? tags
                  .map(([field, expression]) => formatTag(field, expression))
                  .join(', ')
              : 'Every transaction'}
          </p>
        </div>

        {limitEntries.map(([frequency, entry]) => (
          <div
            key={frequency}
            className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between"
          >
            <p className="text-slate-500 text-sm dark:text-slate-100">
              {frequencyLabels[frequency as BusinessLimitFrequency] ??
                formatSelectOptionItem(frequency)}
            </p>
            <p
              data-cy={
                dataCy ? `${dataCy}-${frequency.toLowerCase()}` : undefined
              }
              className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
            >
              {formatLimitEntry(entry)}
            </p>
          </div>
        ))}

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Date Created
          </p>
          <p
            data-cy={dataCy ? `${dataCy}-date-created` : undefined}
            className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
          >
            {formatDate.DDMonYYYY(item?.createdAt?.toString() || '')}
          </p>
        </div>
      </div>

      <div className="text-sm text-slate-500 w-full pt-5 sm:pt-5">
        <div className="flex justify-end gap-4">
          <PermissionsProvider
            permission={AfriexAdminPermission.BusinessLimitManage}
          >
            <LimitUpdateButton
              initialData={item}
              businessId={businessId}
              dataCy={dataCy}
            />
          </PermissionsProvider>
        </div>
      </div>
    </div>
  );
};

export default LimitSummary;
